import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { buscarComboPorCodigo } from "../../services/auth.service";
import {
  buscarContasSpedListaPorCnpj,
  processarRelatorio,
} from "../../services/api-relatorios.service";
import MensagemErro from "../MensagemErro";
import styles from "../BotaoRelatorio/styles.module.css";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";

const FORMATO_DATA = /^\d{4}-\d{2}-\d{2}$/;
const CNPJ_DIGITOS = /^\d{14}$/;

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

async function carregarCombo(codCombo) {
  const response = await buscarComboPorCodigo(codCombo);
  const combosFormat = (response.detalhes.itensCombo || [])
    .filter((item) => item)
    .map((item) => {
      const split = item.split("/|/");
      return { id: split[0], detalhes: split[1] };
    });
  return combosFormat.filter(
    (combo, index, self) =>
      index === self.findIndex((t) => t.detalhes === combo.detalhes)
  );
}

/**
 * Formulário Conciliação SPED: grupo → empresa (CNPJ no `id` do combo EMPRESA_POR_GRUPO_*), período, contas (multi-select).
 */
export default function ExtracaoConciliacaoSpedForm({
  idRelatorio,
  onClose,
  onFinished,
}) {
  const [dataInicial, setDataInicial] = useState("");
  const [dataFinal, setDataFinal] = useState("");
  const [grupos, setGrupos] = useState([]);
  const [grupoSelecionado, setGrupoSelecionado] = useState(null);
  const [listaEmpresas, setListaEmpresas] = useState([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState(null);
  const [opcoesContas, setOpcoesContas] = useState([]);
  const [contasSelecionadas, setContasSelecionadas] = useState([]);
  const [loadingContas, setLoadingContas] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackErro, setSnackErro] = useState({ open: false, msg: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const lista = await carregarCombo("GRUPOECONOMICO");
        if (!cancelled) setGrupos(lista);
      } catch (e) {
        console.log(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!grupoSelecionado) {
      setListaEmpresas([]);
      setEmpresaSelecionada(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const lista = await carregarCombo(
          `EMPRESA_POR_GRUPO_${grupoSelecionado.id}`
        );
        if (!cancelled) {
          setListaEmpresas(lista);
          setEmpresaSelecionada(null);
        }
      } catch (e) {
        console.log(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [grupoSelecionado]);

  useEffect(() => {
    if (!empresaSelecionada?.id) {
      setOpcoesContas([]);
      setContasSelecionadas([]);
      setLoadingContas(false);
      return;
    }
    const cnpj = String(empresaSelecionada.id).replace(/\D/g, "");
    if (!CNPJ_DIGITOS.test(cnpj)) {
      setOpcoesContas([]);
      setContasSelecionadas([]);
      setLoadingContas(false);
      return;
    }
    let cancelled = false;
    setLoadingContas(true);
    (async () => {
      try {
        const res = await buscarContasSpedListaPorCnpj(cnpj);
        const contas = res?.contas ?? [];
        if (!cancelled) {
          setOpcoesContas(contas);
          setContasSelecionadas([]);
        }
      } catch (e) {
        if (!cancelled) {
          setOpcoesContas([]);
          setContasSelecionadas([]);
          setSnackErro({
            open: true,
            msg:
              e?.response?.data?.mensagem ||
              e?.message ||
              "Não foi possível carregar as contas de resultado.",
          });
        }
      } finally {
        if (!cancelled) setLoadingContas(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [empresaSelecionada]);

  const selecionarTodasContas = () => {
    setContasSelecionadas((prev) => {
      if (prev.length === opcoesContas.length) {
        return [];
      }
      return [...opcoesContas];
    });
  };

  const handleGerar = async () => {
    const idRelatorioNum = Number(idRelatorio);
    if (idRelatorio == null || idRelatorio === "" || !Number.isFinite(idRelatorioNum)) {
      setSnackErro({
        open: true,
        msg: "Relatório inválido. Feche e abra novamente ou atualize a página.",
      });
      return;
    }

    if (!grupoSelecionado) {
      setSnackErro({ open: true, msg: "Selecione o grupo econômico." });
      return;
    }

    if (!dataInicial || !FORMATO_DATA.test(dataInicial)) {
      setSnackErro({
        open: true,
        msg: "Informe a data inicial (YYYY-MM-DD).",
      });
      return;
    }
    if (!dataFinal || !FORMATO_DATA.test(dataFinal)) {
      setSnackErro({
        open: true,
        msg: "Informe a data final (YYYY-MM-DD).",
      });
      return;
    }
    if (dataFinal < dataInicial) {
      setSnackErro({
        open: true,
        msg: "A data final não pode ser anterior à data inicial.",
      });
      return;
    }

    if (!empresaSelecionada?.id) {
      setSnackErro({
        open: true,
        msg:
          listaEmpresas.length === 0
            ? "Não há empresas disponíveis para o grupo selecionado."
            : "Selecione uma empresa.",
      });
      return;
    }

    const cnpj = String(empresaSelecionada.id).replace(/\D/g, "");
    if (!CNPJ_DIGITOS.test(cnpj)) {
      setSnackErro({
        open: true,
        msg: "CNPJ da empresa inválido (esperado 14 dígitos).",
      });
      return;
    }

    if (!contasSelecionadas.length) {
      setSnackErro({
        open: true,
        msg: "Selecione ao menos uma conta de resultado.",
      });
      return;
    }

    const contas = contasSelecionadas.map((c) => String(c.id));

    setLoading(true);
    try {
      const res = await processarRelatorio({
        idRelatorio: idRelatorioNum,
        params: {
          cnpj,
          dataInicial,
          dataFinal,
          contas,
        },
      });
      if (res?.sucesso) {
        const msg =
          res?.mensagem ||
          "Pedido aceito; o relatório será processado em fila.";
        if (onFinished) onFinished(true, msg);
        else onClose();
      } else {
        setSnackErro({
          open: true,
          msg: res?.mensagem || "Não foi possível enviar o pedido.",
        });
      }
    } catch (err) {
      const msg =
        err?.response?.data?.mensagem ||
        err?.message ||
        "Erro ao solicitar a exportação.";
      setSnackErro({ open: true, msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        <Autocomplete
          options={grupos}
          value={grupoSelecionado}
          onChange={(_, v) => setGrupoSelecionado(v)}
          getOptionLabel={(o) => o.detalhes}
          isOptionEqualToValue={(a, b) => a?.id === b?.id}
          renderInput={(params) => (
            <TextField {...params} label="Grupo econômico" />
          )}
        />

        {listaEmpresas.length > 0 && (
          <Autocomplete
            options={listaEmpresas}
            value={empresaSelecionada}
            onChange={(_, v) => setEmpresaSelecionada(v)}
            getOptionLabel={(o) => o.detalhes}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            renderInput={(params) => (
              <TextField {...params} label="Empresa" />
            )}
          />
        )}

        <Box sx={{ display: "flex", flexDirection: "row", gap: 2, width: 1 }}>
          <TextField
            label="Data inicial"
            type="date"
            value={dataInicial}
            onChange={(e) => setDataInicial(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Data final"
            type="date"
            value={dataFinal}
            onChange={(e) => setDataFinal(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Contas (resultado)
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={selecionarTodasContas}
              disabled={loadingContas || opcoesContas.length === 0}
            >
              {contasSelecionadas.length === opcoesContas.length &&
              opcoesContas.length > 0
                ? "Limpar seleção"
                : "Selecionar todas"}
            </Button>
          </Box>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={opcoesContas}
            value={contasSelecionadas}
            onChange={(_, v) => setContasSelecionadas(v)}
            getOptionLabel={(o) => o.detalhes || String(o.id)}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            loading={loadingContas}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.detalhes}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Contas contábeis"
                placeholder={
                  loadingContas ? "Carregando…" : "Pesquisar ou selecionar"
                }
              />
            )}
          />
        </Box>

        <div className={styles.button_container}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<DescriptionIcon />}
            onClick={handleGerar}
            disabled={loading || loadingContas}
            sx={{ backgroundColor: "#18316E" }}
          >
            {loading ? "Enviando…" : "Gerar"}
          </Button>
        </div>
      </Box>

      <MensagemErro
        open={snackErro.open}
        handleClose={() => setSnackErro({ open: false, msg: "" })}
        msg={snackErro.msg}
      />
    </>
  );
}
